/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/control-has-associated-label */

import React, { Fragment, useState, useCallback, useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import WarningIcon from 'mdi-react/WarningCircleIcon';

import * as S from './styles';

export enum Variants {
  rate = 'rate',
  scale = 'scale',
  boolean = 'boolean',
}

export enum Colors {
  negative = 'negative',
  neutral = 'neutral',
  positive = 'positive',
}

type Props = {
  currentValue?: number | null;
  disabledOptions?: number[];
  labels?: string[];
  name: string;
  onClick?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  options: number[];
  optionValues?: string[];
  required?: boolean;
  size?: 'small' | 'medium';
  variant: Variants;
};

const ratingColorMap = [
  Colors.negative,
  Colors.negative,
  Colors.neutral,
  Colors.positive,
  Colors.positive,
];

const MultiRadio = ({
  currentValue,
  disabledOptions,
  labels = [],
  name,
  onClick,
  options,
  optionValues = [],
  required,
  size = 'medium',
  variant,
}: Props) => {
  const { t } = useContext(AppContext);
  const [selected, setSelected] = useState(
    currentValue ? optionValues[currentValue - 1] : undefined
  );
  const [ratingColor, setRatingColor] = useState(
    currentValue ? ratingColorMap[currentValue - 1] : Colors.neutral
  );
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(currentValue);

  const getSize = () => {
    switch (size) {
      case 'small':
        return 20;
      case 'medium':
      default:
        return 32;
    }
  };

  const iconSize = getSize();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checkValidity()) {
        setError(null);
      }

      setSelected(e.target.dataset.value);
      setChecked(parseInt(e.target.value, 10));

      if (variant === Variants.rate) {
        const index = Number(e.target.value) - 1;
        setRatingColor(ratingColorMap[index]);
      }

      if (onClick) onClick(e);
    },
    [onClick, variant]
  );

  const getIsDisabled = (option: number) => {
    return disabledOptions?.length
      ? disabledOptions?.find(item => item === option) !== undefined
      : false;
  };

  const handleInvalidity = useCallback(
    (e: React.InvalidEvent<HTMLInputElement>) => {
      if (error === e.target.name) return;

      setError(e.target.name);
    },
    [error]
  );

  if (variant === Variants.rate) {
    return (
      <>
        <S.MultiRadio variant={variant} onInvalid={handleInvalidity}>
          {options.map((option, index) => {
            switch (option) {
              case 1: {
                return (
                  <Fragment key={option}>
                    <S.RadioRating
                      type="radio"
                      name={name}
                      id={`${name}:${option}`}
                      value={option}
                      data-value={optionValues[index]}
                      required={required}
                      onChange={handleChange}
                      tabIndex={0}
                      checked={checked === option}
                      $size={size}
                      disabled={getIsDisabled(option)}
                    />
                    <label htmlFor={`${name}:${option}`}>
                      <S.EmoticonAngryIcon
                        showError={error === name}
                        size={iconSize}
                        ariaLabel={`${name}:${option}`}
                      />
                    </label>
                  </Fragment>
                );
              }
              case 2: {
                return (
                  <Fragment key={option}>
                    <S.RadioRating
                      type="radio"
                      name={name}
                      id={`${name}:${option}`}
                      value={option}
                      data-value={optionValues[index]}
                      required={required}
                      onChange={handleChange}
                      tabIndex={0}
                      checked={checked === option}
                      $size={size}
                      disabled={getIsDisabled(option)}
                    />
                    <label htmlFor={`${name}:${option}`}>
                      <S.EmoticonDissatisfiedIcon
                        showError={error === name}
                        size={iconSize}
                        ariaLabel={`${name}:${option}`}
                      />
                    </label>
                  </Fragment>
                );
              }
              case 3: {
                return (
                  <Fragment key={option}>
                    <S.RadioRating
                      type="radio"
                      name={name}
                      id={`${name}:${option}`}
                      value={option}
                      data-value={optionValues[index]}
                      required={required}
                      onChange={handleChange}
                      tabIndex={0}
                      checked={checked === option}
                      $size={size}
                      disabled={getIsDisabled(option)}
                    />
                    <label htmlFor={`${name}:${option}`}>
                      <S.EmoticonNeutralIcon
                        showError={error === name}
                        size={iconSize}
                        ariaLabel={`${name}:${option}`}
                      />
                    </label>
                  </Fragment>
                );
              }

              case 4: {
                return (
                  <Fragment key={option}>
                    <S.RadioRating
                      type="radio"
                      name={name}
                      id={`${name}:${option}`}
                      value={option}
                      data-value={optionValues[index]}
                      required={required}
                      onChange={handleChange}
                      tabIndex={0}
                      checked={checked === option}
                      $size={size}
                      disabled={getIsDisabled(option)}
                    />
                    <label htmlFor={`${name}:${option}`}>
                      <S.EmoticonSmilingIcon
                        showError={error === name}
                        size={iconSize}
                        ariaLabel={`${name}:${option}`}
                      />
                    </label>
                  </Fragment>
                );
              }

              case 5: {
                return (
                  <Fragment key={option}>
                    <S.RadioRating
                      type="radio"
                      name={name}
                      id={`${name}:${option}`}
                      value={option}
                      data-value={
                        optionValues.length ? optionValues[index] : undefined
                      }
                      required={required}
                      onChange={handleChange}
                      tabIndex={0}
                      checked={checked === option}
                      $size={size}
                      disabled={getIsDisabled(option)}
                    />
                    <label htmlFor={`${name}:${option}`}>
                      <S.EmoticonLaughingIcon
                        showError={error === name}
                        size={iconSize}
                        ariaLabel={`${name}:${option}`}
                      />
                    </label>
                  </Fragment>
                );
              }
              default:
                return null;
            }
          })}

          {selected && (
            <S.SelectedRating color={ratingColor}>{selected}</S.SelectedRating>
          )}
        </S.MultiRadio>

        {error === name && (
          <S.Error>
            <WarningIcon size={16} /> {t('reviews', 'form.error-radio')}
          </S.Error>
        )}
      </>
    );
  }

  if (variant === Variants.scale) {
    return (
      <>
        <S.MultiRadio
          variant={variant}
          onInvalid={handleInvalidity}
          hasSelected={Boolean(optionValues.length)}
          showError={error === name}
        >
          {optionValues.length ? (
            <S.Selected>
              {selected ?? t('reviews', 'form.question-nothing-selected')}
            </S.Selected>
          ) : null}

          <S.Inputs>
            {options.map((option, index) => {
              return (
                <Fragment key={option}>
                  <S.ScaleRadio
                    type="radio"
                    name={name}
                    id={`${name}:${option}`}
                    value={option}
                    data-value={
                      optionValues.length ? optionValues[index] : undefined
                    }
                    required={required}
                    onChange={handleChange}
                    alignRight={index + 1 === options.length}
                    alignLeft={index === 0}
                    showError={error === name}
                    tabIndex={0}
                    checked={checked === option}
                  />
                  <label
                    htmlFor={`${name}:${option}`}
                    data-label={labels.length ? labels[index] : null}
                  />
                </Fragment>
              );
            })}
          </S.Inputs>
        </S.MultiRadio>

        {error === name && (
          <S.Error>
            <WarningIcon size={16} /> {t('reviews', 'form.error-radio')}
          </S.Error>
        )}
      </>
    );
  }

  if (variant === Variants.boolean) {
    return (
      <>
        <S.MultiRadio
          variant={variant}
          onInvalid={handleInvalidity}
          showError={error === name}
        >
          <S.Inputs>
            <>
              <S.BooleanRadio
                type="radio"
                name={name}
                id={`${name}:1`}
                value="1"
                data-value={t('reviews', 'form.boolean-yes')}
                required={required}
                onChange={handleChange}
                tabIndex={0}
                checked={checked === 1}
              />
              <label htmlFor={`${name}:1`}>
                {t('reviews', 'form.boolean-yes')}
              </label>
            </>

            <>
              <S.BooleanRadio
                type="radio"
                name={name}
                id={`${name}:0`}
                value="0"
                data-value={t('reviews', 'form.boolean-no')}
                required={required}
                onChange={handleChange}
                tabIndex={0}
                checked={checked === 0}
              />
              <label htmlFor={`${name}:0`}>
                {t('reviews', 'form.boolean-no')}
              </label>
            </>

            {options.length > 2 ? (
              <>
                <S.BooleanRadio
                  type="radio"
                  name={name}
                  id={`${name}:2`}
                  value="2"
                  data-value={t('reviews', 'form.boolean-unknown')}
                  required={required}
                  onChange={handleChange}
                  tabIndex={0}
                  checked={checked === 2}
                />
                <label htmlFor={`${name}:2`}>
                  {t('reviews', 'form.boolean-no')}
                </label>
              </>
            ) : null}
          </S.Inputs>
        </S.MultiRadio>

        {error === name && (
          <S.Error>
            <WarningIcon size={16} /> {t('reviews', 'form.error-radio')}
          </S.Error>
        )}
      </>
    );
  }

  return null;
};

export default MultiRadio;
